import type { PetManifest } from "codexpetlab-core";

export interface LocalPetRecord {
  id: string;
  manifest: PetManifest;
  spritesheet: Blob;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

const DB_NAME = "codex-pet-lab";
const DB_VERSION = 1;
const STORE_NAME = "local-pets";

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME, { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Unable to open local pet database"));
  });
}

export async function listLocalPets(): Promise<LocalPetRecord[]> {
  const database = await openDatabase();
  try {
    const records = await requestResult(database.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).getAll()) as LocalPetRecord[];
    return records.filter((record) => record.manifest.spriteVersionNumber === 2).sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  } finally {
    database.close();
  }
}

export async function saveLocalPet(record: Omit<LocalPetRecord, "createdAt" | "updatedAt">): Promise<LocalPetRecord> {
  const database = await openDatabase();
  try {
    const existing = await requestResult(database.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).get(record.id)) as LocalPetRecord | undefined;
    const now = new Date().toISOString();
    const saved: LocalPetRecord = { ...record, createdAt: existing?.createdAt ?? now, updatedAt: now };
    await requestResult(database.transaction(STORE_NAME, "readwrite").objectStore(STORE_NAME).put(saved));
    return saved;
  } finally {
    database.close();
  }
}

export async function deleteLocalPet(id: string): Promise<void> {
  const database = await openDatabase();
  try {
    await requestResult(database.transaction(STORE_NAME, "readwrite").objectStore(STORE_NAME).delete(id));
  } finally {
    database.close();
  }
}
